import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, X, Folder, Tag, Upload, Loader2, Image as ImageIcon, ArrowLeft } from 'lucide-react'
import { categoryService, CreateCategoryDto, UpdateCategoryDto, Category } from '@/services/categoryService'
import { getImageUrl } from '@/config/imageConfig'
import { DetailSection } from '../ui/DetailSection'

const CategoryForm = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(false)
  const [loadingCategory, setLoadingCategory] = useState(isEditMode)
  const [error, setError] = useState<string | null>(null)
  const [parentCategories, setParentCategories] = useState<Category[]>([])

  const [formData, setFormData] = useState<CreateCategoryDto | UpdateCategoryDto>({
    name: '',
    description: '',
    parent_category_id: null,
  })

  const [isActive, setIsActive] = useState(true)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)

  // Load parent categories
  useEffect(() => {
    const loadParentCategories = async () => {
      try {
        const response = await categoryService.getAll({})
        // Exclude current category from parent list when editing
        const categories = id
          ? response.data.filter(c => c.category_id !== id)
          : response.data
        setParentCategories(categories)
      } catch (err) {
        console.error('Failed to load parent categories:', err)
      }
    }
    loadParentCategories()
  }, [id])

  // Load category data when editing
  useEffect(() => {
    if (isEditMode && id) {
      const loadCategory = async () => {
        try {
          setLoadingCategory(true)
          const response = await categoryService.getById(id)
          const category = response.data
          setFormData({
            name: category.name,
            description: category.description || '',
            parent_category_id: category.parent_category_id,
          })
          setIsActive(category.is_active)
          if (category.logo_url) {
            setLogoPreview(getImageUrl(`/uploads/categories/${category.logo_url}`))
          }
        } catch (err: any) {
          console.error('Error loading category:', err)
          setError(err.response?.data?.message || 'Échec du chargement de la catégorie')
        } finally {
          setLoadingCategory(false)
        }
      }
      loadCategory()
    }
  }, [id, isEditMode])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'parent_category_id' ? (value === '' ? null : value) : value
    }))
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setLogoFile(file)
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file)
      setLogoPreview(previewUrl)
    }
  }

  const handleRemoveLogo = () => {
    setLogoFile(null)
    setLogoPreview(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      if (isEditMode && id) {
        const updateData: UpdateCategoryDto = {
          ...formData,
          is_active: isActive,
        }
        // Only include logo if a new file was selected
        if (logoFile) {
          updateData.logo = logoFile
        }
        await categoryService.update(id, updateData)
      } else {
        const createData: CreateCategoryDto = {
          ...formData,
        } as CreateCategoryDto
        if (logoFile) {
          createData.logo = logoFile
        }
        await categoryService.create(createData)
      }
      navigate('/categories')
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} category:`, err)
      setError(err.response?.data?.message || `Échec de ${isEditMode ? 'la mise à jour' : 'la création'} de la catégorie`)
    } finally {
      setLoading(false)
    }
  }

  if (loadingCategory) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/categories')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour à la liste
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
          </h1>
          <p className="text-gray-600">
            {isEditMode
              ? 'Modifiez les informations de la catégorie'
              : 'Ajoutez une nouvelle catégorie de produits à la plateforme'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Informations de la catégorie */}
          <DetailSection title="Informations de la catégorie" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de la catégorie *
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  value={(formData as any).name}
                  onChange={handleChange}
                  required
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Nom de la catégorie"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={(formData as any).description || ''}
                onChange={handleChange}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                placeholder="Description de la catégorie"
              />
            </div>

            {/* Catégorie parente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Catégorie parente
              </label>
              <div className="relative">
                <Folder className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  name="parent_category_id"
                  value={(formData as any).parent_category_id || ''}
                  onChange={handleChange}
                  className="pl-10 w-full rounded-lg border border-gray-300 py-2 pr-3 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Aucune (catégorie racine)</option>
                  {parentCategories.map((category) => (
                    <option key={category.category_id} value={category.category_id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Laissez vide pour créer une catégorie racine
              </p>
            </div>
          </DetailSection>

          {/* Logo et statut */}
          <DetailSection title="Logo et statut" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

            {/* Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Logo de la catégorie
              </label>
              <div className="space-y-3">
                {/* Logo preview */}
                {logoPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={logoPreview}
                      alt="Aperçu du logo"
                      className="h-24 w-24 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none"
                      title="Supprimer le logo"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}

                {/* File input */}
                <div className="relative">
                  <input
                    type="file"
                    id="logo"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="logo"
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {logoPreview ? 'Changer le logo' : 'Téléverser un logo'}
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  Formats acceptés : JPG, PNG, GIF, WebP. Max 10 Mo.
                </p>
              </div>
            </div>

            {/* Statut (uniquement en mode édition) */}
            {isEditMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut
                </label>
                <div className="flex items-center gap-4 pt-2">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="is_active"
                      checked={isActive === true}
                      onChange={() => setIsActive(true)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Actif</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="is_active"
                      checked={isActive === false}
                      onChange={() => setIsActive(false)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Inactif</span>
                  </label>
                </div>
              </div>
            )}
          </DetailSection>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/categories')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 inline mr-2" />
                {isEditMode ? 'Mettre à jour la catégorie' : 'Créer la catégorie'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default CategoryForm
